import { Injectable } from '@angular/core';
import { Client } from '@df/core/service/client';
import { Logger } from '@df/core/service/logger';
import { contains } from '@df/util/fn/contains';
import { ICmsOutletComponent } from '@icms/client/assets/outlet/icms-outlet.component';
import { ICmsDataChangeService } from '@icms/core/data/change/icms-data-change.service';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { ICmsContentService } from '../icms-content.service';
import { NICmsContentComponent } from '../interfaces/icms-content-component.interfaces';
import { NICmsContentField } from '../interfaces/icms-content-field.interfaces';

export interface IComponentError {
    copy: string;
    level: 'error' | 'info';
    overrideId?: string;
}

export interface IValidtionEvent {
    data: NICmsContentComponent.IData;
    meta: NICmsContentComponent.IMeta;
    outlet: ICmsOutletComponent | undefined;
    error: IComponentError | undefined;
}

export interface ObjectHook {
    state: BehaviorSubject<IComponentError | undefined>;
    subscriptions: Subscription;
    trigger$: Subject<void>;
    outlet: ICmsOutletComponent | undefined;
    ignoreErrors: string[]; // override errors and display anyway (when user requested)
}

@Injectable({
    providedIn: 'root'
})
export class ICmsContentComponentValidationService {
    private validatedObjects = new Map<NICmsContentComponent.IData, ObjectHook>();

    readonly validation$ = new Subject<IValidtionEvent>();

    constructor(
        private iCmsContentService: ICmsContentService,
        private logger: Logger,
        private iCmsDataChangeService: ICmsDataChangeService,
        private client: Client
    ) {}

    getObjectHook(
        data: NICmsContentComponent.IData,
        create = false,
        meta?: NICmsContentComponent.IMeta,
        outlet?: ICmsOutletComponent
    ): ObjectHook | undefined {
        let hook = this.validatedObjects.get(data);

        // outletOptions: NICmsContentComponent.IOutletOptions | undefined;

        if (!hook && create) {
            const state = new BehaviorSubject<IComponentError | undefined>(undefined);

            hook = {
                state,
                subscriptions: new Subscription(),
                trigger$: new Subject<void>(),
                outlet,
                ignoreErrors: []
            };

            this.validatedObjects.set(data, hook);

            if (!meta) {
                meta = this.iCmsContentService.getComponentMeta(data.id);
            }

            if (!meta) {
                this.logger.warn('Component meta not found', data);
            } else {
                const validate = () => {
                    const error = this.getNoDisplayReason(data, meta!, hook!.outlet);
                    if (state.getValue() !== error) {
                        state.next(error);
                    }
                };

                hook.subscriptions.add(
                    this.iCmsDataChangeService.afterChange$.subscribe(event => {
                        if (contains(data, event.changedObject, Infinity)) {
                            validate();
                        }
                    })
                );

                hook.subscriptions.add(hook?.trigger$.subscribe(validate));

                if (outlet) {
                    hook.subscriptions.add(outlet.optionsChange.subscribe(() => validate()));
                }

                validate();
            }
        }

        return hook;
    }

    getState$(
        data: NICmsContentComponent.IData,
        meta: NICmsContentComponent.IMeta | undefined,
        outlet?: ICmsOutletComponent
    ): BehaviorSubject<IComponentError | undefined> {
        return this.getObjectHook(data, true, meta, outlet)!.state;
    }

    overrideError(data: NICmsContentComponent.IData, error: string, outlet: ICmsOutletComponent | undefined) {
        const hook = this.getObjectHook(data, false, undefined, outlet);
        if (hook) {
            if (!hook.ignoreErrors.includes(error)) {
                hook.ignoreErrors.push(error);
                hook.trigger$.next();
            }
        }
    }

    /**
     * Return error string or nothing if valid
     */
    protected getNoDisplayReason(
        data: NICmsContentComponent.IData,
        meta: NICmsContentComponent.IMeta,
        outlet: ICmsOutletComponent | undefined
    ): IComponentError | undefined {
        if (data && typeof data === 'object') {
            if ((data as NICmsContentComponent.IDataWithStyles).styles?.hidden) {
                return {
                    copy: 'Component is hidden',
                    level: 'info'
                };
            }

            const missingField = this.findMissingField(meta?.fields, data, outlet);
            if (missingField) {
                const hook = this.getObjectHook(data, false, meta, outlet);
                const overrideId = 'minConfig';
                if (!hook?.ignoreErrors.includes(overrideId)) {
                    return {
                        copy: `Minimal component configuration not set (${missingField.title})`,
                        level: 'error',
                        overrideId
                    };
                }
            }

            const event: IValidtionEvent = {
                data,
                meta,
                outlet,
                error: undefined
            };

            this.validation$.next(event);

            return event.error;
        }

        return;
    }

    private hasMissingFields(fields: NICmsContentField.IField[] | undefined, dataSet: any, outlet: ICmsOutletComponent | undefined) {
        return !!this.findMissingField(fields, dataSet, outlet);
    }

    findMissingField(
        fields: NICmsContentField.IField[] | undefined,
        dataSet: any,
        outlet: ICmsOutletComponent | undefined
    ): NICmsContentField.IField | undefined {
        return fields?.find(field => this.isFieldEmpty(dataSet, field, outlet));
    }

    /**
     * Filters fields to return only ones that can be edited with the current setup
     */
    filterVisibleFields<TContainer = any>(
        fields: NICmsContentField.IField<any, TContainer>[],
        data?: TContainer,
        outletOptions?: NICmsContentComponent.IOutletOptions
    ) {
        return fields.filter(field => this.canShowField(field, data, outletOptions));
    }

    private canShowField<TField = any, TContainer = any>(
        field: NICmsContentField.IField<TField, TContainer>,
        data?: TContainer,
        outletOptions: NICmsContentComponent.IOutletOptions = {}
    ): boolean {
        return !field.canShow || (data && field.canShow(data, outletOptions)) || false;
    }

    private isFieldEmpty(dataSet: any | undefined, field: NICmsContentField.IField, outlet: ICmsOutletComponent | undefined): boolean {
        if (!this.canShowField(field, dataSet, outlet?.options)) {
            return false;
        }
        const value = get(dataSet, field.key.split('.'));
        const isEmptyValue = isEmpty(value);

        const isRequired = field.required && (typeof field.required === 'boolean' || field.required(dataSet));

        if (isEmptyValue && !isRequired) {
            return false;
        }

        // temporarly we only validate repeater in CMS not to break websites
        if (this.client.isCms) {
            if (field.type === NICmsContentField.EFieldType.repeater) {
                const repeaterField = field as NICmsContentField.IRepeaterField;
                const minSize = repeaterField.minSize || (isRequired ? 1 : 0);
                const maxSize = repeaterField.maxSize
                    ? typeof repeaterField.maxSize === 'number'
                        ? repeaterField.maxSize
                        : repeaterField.maxSize(dataSet)
                    : Infinity;

                if (!value || !Array.isArray(value)) {
                    return true;
                }

                // invalid, too many fields
                if (value.length > maxSize || value.length < minSize) {
                    return true;
                }

                // valid fields
                const subField = value.find(row => this.hasMissingFields(repeaterField.fields, row, outlet));
                return subField ? true : false;
            }
        }

        switch (typeof value) {
            case 'string':
                return !value;
            case 'number':
                return isNaN(value);
            case 'boolean':
                return false;
            default:
                return value === null || value === undefined;
        }

        return false;
    }
}
